import { TenderState } from "../types/tender.types";

export interface VersionHistoryItem {
  id: string;
  timestamp: Date;
  data: TenderState;
  changeDescription: string;
  changeType: "manual" | "auto" | "calculation" | "import";
  size: number; // Data size in bytes
  checksum: string; // For data integrity verification
}

export interface BackupMetadata {
  id: string;
  timestamp: Date;
  projectName: string;
  client: string;
  size: number;
  version: string;
  reason: "auto-save" | "manual-save" | "pre-calculation" | "crash-recovery";
}

export class DataRecovery {
  private static readonly MAX_VERSIONS = 5;
  private static readonly MAX_STORAGE_SIZE = 10 * 1024 * 1024; // 10MB
  private static readonly STORAGE_KEY_PREFIX = "mj-estimator-version-";
  private static readonly BACKUP_KEY_PREFIX = "mj-estimator-backup-";
  private static readonly METADATA_KEY = "mj-estimator-metadata";

  /**
   * Save version to history
   */
  static saveVersion(
    data: TenderState,
    changeDescription: string,
    changeType: VersionHistoryItem["changeType"] = "manual"
  ): void {
    try {
      const version: VersionHistoryItem = {
        id: this.generateVersionId(),
        timestamp: new Date(),
        data: { ...data },
        changeDescription,
        changeType,
        size: this.calculateDataSize(data),
        checksum: this.calculateChecksum(data),
      };

      // Get existing versions
      const existingVersions = this.getVersionHistory();

      // Add new version
      const updatedVersions = [version, ...existingVersions];

      // Maintain version limit and storage size
      const prunedVersions = this.pruneVersions(updatedVersions);

      // Save to localStorage
      this.saveVersionsToStorage(prunedVersions);

      // Update metadata
      this.updateMetadata(prunedVersions);
    } catch (error) {
      console.error("Failed to save version:", error);

      // Fallback to session storage
      this.saveToSessionStorage(data, changeDescription);
    }
  }

  /**
   * Get version history
   */
  static getVersionHistory(): VersionHistoryItem[] {
    try {
      const metadata = this.getMetadata();
      const versions: VersionHistoryItem[] = [];

      for (const meta of metadata) {
        const versionData = localStorage.getItem(
          `${this.STORAGE_KEY_PREFIX}${meta.id}`
        );
        if (versionData) {
          const version = JSON.parse(versionData);
          // Convert timestamp string back to Date
          version.timestamp = new Date(version.timestamp);
          version.data.dateCreated = new Date(version.data.dateCreated);
          version.data.lastModified = new Date(version.data.lastModified);
          versions.push(version);
        }
      }

      return versions.sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
      );
    } catch (error) {
      console.error("Failed to get version history:", error);
      return [];
    }
  }

  /**
   * Restore from version
   */
  static restoreFromVersion(versionId: string): TenderState | null {
    try {
      const versionData = localStorage.getItem(
        `${this.STORAGE_KEY_PREFIX}${versionId}`
      );
      if (!versionData) {
        throw new Error(`Version ${versionId} not found`);
      }

      const version: VersionHistoryItem = JSON.parse(versionData);

      // Verify data integrity
      const currentChecksum = this.calculateChecksum(version.data);
      if (currentChecksum !== version.checksum) {
        console.warn("Data integrity check failed for version", versionId);
        // Continue anyway, but log the issue
      }

      // Convert dates back to Date objects
      const restoredData = {
        ...version.data,
        dateCreated: new Date(version.data.dateCreated),
        lastModified: new Date(version.data.lastModified),
      };

      return restoredData;
    } catch (error) {
      console.error("Failed to restore from version:", error);
      return null;
    }
  }

  /**
   * Create backup before major operations
   */
  static createBackup(
    data: TenderState,
    reason: BackupMetadata["reason"] = "manual-save"
  ): string {
    try {
      const backupId = this.generateBackupId();
      const backup = {
        data,
        metadata: {
          id: backupId,
          timestamp: new Date(),
          projectName: data.projectName,
          client: data.client,
          size: this.calculateDataSize(data),
          version: "1.0.0", // Application version
          reason,
        },
      };

      // Save to multiple storage locations for redundancy
      localStorage.setItem(
        `${this.BACKUP_KEY_PREFIX}${backupId}`,
        JSON.stringify(backup)
      );
      sessionStorage.setItem(
        `${this.BACKUP_KEY_PREFIX}${backupId}`,
        JSON.stringify(backup)
      );

      return backupId;
    } catch (error) {
      console.error("Failed to create backup:", error);
      throw error;
    }
  }

  /**
   * Restore from backup
   */
  static restoreFromBackup(backupId: string): TenderState | null {
    try {
      // Try localStorage first, then sessionStorage
      let backupData =
        localStorage.getItem(`${this.BACKUP_KEY_PREFIX}${backupId}`) ||
        sessionStorage.getItem(`${this.BACKUP_KEY_PREFIX}${backupId}`);

      if (!backupData) {
        throw new Error(`Backup ${backupId} not found`);
      }

      const backup = JSON.parse(backupData);

      // Convert dates back to Date objects
      const restoredData = {
        ...backup.data,
        dateCreated: new Date(backup.data.dateCreated),
        lastModified: new Date(backup.data.lastModified),
      };

      return restoredData;
    } catch (error) {
      console.error("Failed to restore from backup:", error);
      return null;
    }
  }

  /**
   * Get available backups
   */
  static getAvailableBackups(): BackupMetadata[] {
    const backups: BackupMetadata[] = [];

    try {
      // Check localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.BACKUP_KEY_PREFIX)) {
          const backupData = localStorage.getItem(key);
          if (backupData) {
            const backup = JSON.parse(backupData);
            backups.push({
              ...backup.metadata,
              timestamp: new Date(backup.metadata.timestamp),
            });
          }
        }
      }

      // Check sessionStorage for additional backups
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(this.BACKUP_KEY_PREFIX)) {
          const backupId = key.replace(this.BACKUP_KEY_PREFIX, "");

          // Skip if already found in localStorage
          if (backups.some((b) => b.id === backupId)) continue;

          const backupData = sessionStorage.getItem(key);
          if (backupData) {
            const backup = JSON.parse(backupData);
            backups.push({
              ...backup.metadata,
              timestamp: new Date(backup.metadata.timestamp),
            });
          }
        }
      }
    } catch (error) {
      console.error("Failed to get available backups:", error);
    }

    return backups.sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );
  }

  /**
   * Detect and recover from crash
   */
  static detectCrashRecovery(): TenderState | null {
    try {
      // Check for unsaved changes marker
      const crashMarker = sessionStorage.getItem("mj-estimator-crash-marker");
      if (!crashMarker) return null;

      // Look for the most recent auto-save or backup
      const backups = this.getAvailableBackups();
      const recentAutoSave = backups.find(
        (b) =>
          b.reason === "auto-save" &&
          Date.now() - b.timestamp.getTime() < 5 * 60 * 1000 // Within 5 minutes
      );

      if (recentAutoSave) {
        // Clear crash marker
        sessionStorage.removeItem("mj-estimator-crash-marker");

        console.log("Crash recovery: restoring from auto-save");
        return this.restoreFromBackup(recentAutoSave.id);
      }

      // Look for version history
      const versions = this.getVersionHistory();
      if (versions.length > 0) {
        sessionStorage.removeItem("mj-estimator-crash-marker");

        console.log("Crash recovery: restoring from version history");
        return versions[0].data;
      }

      return null;
    } catch (error) {
      console.error("Failed to detect crash recovery:", error);
      return null;
    }
  }

  /**
   * Mark application as potentially crashed
   */
  static markPotentialCrash(): void {
    try {
      sessionStorage.setItem(
        "mj-estimator-crash-marker",
        Date.now().toString()
      );
    } catch (error) {
      console.error("Failed to mark potential crash:", error);
    }
  }

  /**
   * Clear crash marker (normal shutdown)
   */
  static clearCrashMarker(): void {
    try {
      sessionStorage.removeItem("mj-estimator-crash-marker");
    } catch (error) {
      console.error("Failed to clear crash marker:", error);
    }
  }

  /**
   * Clean up old data
   */
  static cleanup(): void {
    try {
      const versions = this.getVersionHistory();
      const prunedVersions = this.pruneVersions(versions);
      this.saveVersionsToStorage(prunedVersions);

      // Clean up old backups (keep last 10)
      const backups = this.getAvailableBackups();
      const backupsToRemove = backups.slice(10);

      backupsToRemove.forEach((backup) => {
        localStorage.removeItem(`${this.BACKUP_KEY_PREFIX}${backup.id}`);
        sessionStorage.removeItem(`${this.BACKUP_KEY_PREFIX}${backup.id}`);
      });

      console.log(
        `Cleanup completed: removed ${backupsToRemove.length} old backups`
      );
    } catch (error) {
      console.error("Failed to cleanup:", error);
    }
  }

  /**
   * Export recovery data
   */
  static exportRecoveryData(): string {
    const data = {
      versions: this.getVersionHistory(),
      backups: this.getAvailableBackups(),
      metadata: this.getMetadata(),
      exportTimestamp: new Date().toISOString(),
    };

    return JSON.stringify(data, null, 2);
  }

  /**
   * Get storage usage statistics
   */
  static getStorageStats(): {
    versionsSize: number;
    backupsSize: number;
    totalSize: number;
    versionsCount: number;
    backupsCount: number;
  } {
    let versionsSize = 0;
    let backupsSize = 0;
    let versionsCount = 0;
    let backupsCount = 0;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;

        const value = localStorage.getItem(key) || "";
        const size = new Blob([value]).size;

        if (key.startsWith(this.STORAGE_KEY_PREFIX)) {
          versionsSize += size;
          versionsCount++;
        } else if (key.startsWith(this.BACKUP_KEY_PREFIX)) {
          backupsSize += size;
          backupsCount++;
        }
      }
    } catch (error) {
      console.error("Failed to calculate storage stats:", error);
    }

    return {
      versionsSize,
      backupsSize,
      totalSize: versionsSize + backupsSize,
      versionsCount,
      backupsCount,
    };
  }

  // Private helper methods
  private static generateVersionId(): string {
    return `v${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private static generateBackupId(): string {
    return `b${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private static calculateDataSize(data: TenderState): number {
    return new Blob([JSON.stringify(data)]).size;
  }

  private static calculateChecksum(data: TenderState): string {
    // Simple checksum using JSON string
    const str = JSON.stringify(data);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  }

  private static pruneVersions(
    versions: VersionHistoryItem[]
  ): VersionHistoryItem[] {
    // Sort by timestamp (newest first)
    const sorted = versions.sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );

    // Keep only the latest versions within size limit
    const pruned: VersionHistoryItem[] = [];
    let totalSize = 0;

    for (const version of sorted) {
      if (pruned.length >= this.MAX_VERSIONS) break;
      if (totalSize + version.size > this.MAX_STORAGE_SIZE) break;

      pruned.push(version);
      totalSize += version.size;
    }

    return pruned;
  }

  private static saveVersionsToStorage(versions: VersionHistoryItem[]): void {
    // Clear old versions first
    this.clearOldVersions();

    // Save each version
    versions.forEach((version) => {
      localStorage.setItem(
        `${this.STORAGE_KEY_PREFIX}${version.id}`,
        JSON.stringify(version)
      );
    });
  }

  private static clearOldVersions(): void {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  }

  private static updateMetadata(versions: VersionHistoryItem[]): void {
    const metadata = versions.map((v) => ({
      id: v.id,
      timestamp: v.timestamp,
      projectName: v.data.projectName,
      client: v.data.client,
      size: v.size,
      version: "1.0.0",
      reason: "version-history" as const,
    }));

    localStorage.setItem(this.METADATA_KEY, JSON.stringify(metadata));
  }

  private static getMetadata(): BackupMetadata[] {
    try {
      const metadata = localStorage.getItem(this.METADATA_KEY);
      if (!metadata) return [];

      return JSON.parse(metadata).map((meta: any) => ({
        ...meta,
        timestamp: new Date(meta.timestamp),
      }));
    } catch (error) {
      return [];
    }
  }

  private static saveToSessionStorage(
    data: TenderState,
    description: string
  ): void {
    try {
      sessionStorage.setItem(
        "mj-estimator-emergency-backup",
        JSON.stringify({
          data,
          timestamp: new Date().toISOString(),
          description,
        })
      );
    } catch (error) {
      console.error("Emergency backup to session storage failed:", error);
    }
  }
}

// Set up automatic crash detection
window.addEventListener("beforeunload", () => {
  DataRecovery.markPotentialCrash();
});

// Clear crash marker on normal load
window.addEventListener("load", () => {
  setTimeout(() => {
    DataRecovery.clearCrashMarker();
  }, 1000);
});

export default DataRecovery;
